import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  Radio, Crown, MapPin, Award, Share2, Send, Bot, Sparkles, X,
  Tag, ArrowRight, MessageSquare, Loader2, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import EmbedRenderer from "@/components/EmbedRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Visitor-facing dedicated LiveCast watch page. */
export default function LivecastPage() {
  const { listingSlug, castSlug } = useParams();
  const [cast, setCast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [registration, setRegistration] = useState(null);
  const sessionId = useRef(`lc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v1/livecasts/public/${listingSlug}/${castSlug}`);
        setCast(r.data);
        track("livecast_listing_view", r.data);
      } catch {
        toast.error("LiveCast not found.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingSlug, castSlug]);

  const track = (event_name, ctx = {}) => {
    axios.post(`${API}/track`, {
      event_name,
      visitor_id: sessionId,
      listing_slug: listingSlug,
      meta: { livecast_slug: castSlug, livecast_id: ctx.id },
    }).catch(() => {});
  };

  if (loading) return <PageShell><div className="text-[#94A3B8] py-20 text-center">Loading…</div></PageShell>;
  if (!cast) return <PageShell><div className="text-center py-20"><h1 className="text-2xl text-white font-bold">LiveCast not found</h1><Link to={`/business/${listingSlug}`} className="text-[#F4B41A] mt-3 inline-block">← Back to listing</Link></div></PageShell>;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <BreadcrumbBar cast={cast} />
        {!registered ? (
          <PreviewAndRegister
            cast={cast}
            onRegistered={(reg) => {
              setRegistration(reg);
              setRegistered(true);
              track("livecast_registration", cast);
            }}
            onTrack={track}
          />
        ) : (
          <WatchRoom cast={cast} registration={registration} onTrack={track} />
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return <div className="min-h-screen bg-[#040814] text-white pt-20">{children}</div>;
}

function BreadcrumbBar({ cast }) {
  const biz = cast.business || {};
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm mb-5 text-[#94A3B8]">
      <Link to={`/business/${biz.slug}`} className="hover:text-white flex items-center gap-1.5">
        {biz.logo_url && <img src={biz.logo_url} alt="" className="w-5 h-5 rounded-md object-cover" />}
        {biz.name}
      </Link>
      <span>›</span>
      <span className="text-[#F4B41A] font-semibold">Best of Best LiveCast™</span>
    </div>
  );
}

/* ============================================================== */
/* Pre-registration preview + lead form                            */
/* ============================================================== */
function PreviewAndRegister({ cast, onRegistered, onTrack }) {
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      {/* Hero preview */}
      <div className="lg:col-span-7 space-y-5">
        <div className="rounded-2xl overflow-hidden border border-[#F4B41A]/30 bg-black shadow-[0_30px_80px_-30px_rgba(244,180,26,0.35)]">
          <div className="aspect-video relative bg-black">
            {cast.video?.thumbnail ? (
              <img src={cast.video.thumbnail} alt={cast.title} className="w-full h-full object-cover" />
            ) : cast.video?.embed_url ? (
              <iframe src={cast.video.embed_url} title="Preview" className="w-full h-full pointer-events-none" style={{ border: 0 }} />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center">
                <Radio className="w-16 h-16 text-[#F4B41A]/60" />
              </div>
            )}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-[10px] uppercase tracking-widest font-bold">
              <span className="w-1.5 h-1.5 bg-[#F4B41A] rounded-full" /> Watch Now
            </div>
            <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F4B41A]/90 text-[#0A0F1C] text-[10px] uppercase tracking-widest font-bold">
              <Crown className="w-3 h-3" /> Best of Best
            </div>
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#F4B41A] font-bold mb-2">Best of Best LiveCast™</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">{cast.title}</h1>
          {cast.short_description && <p className="text-[#C7D2E0] mt-3 leading-relaxed">{cast.short_description}</p>}
          {cast.long_description && <p className="text-[#94A3B8] mt-4 leading-relaxed whitespace-pre-line">{cast.long_description}</p>}
          {(cast.host_name || cast.host_title) && (
            <div className="mt-5 flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10 w-fit">
              {cast.host_avatar_url && <img src={cast.host_avatar_url} alt={cast.host_name} className="w-10 h-10 rounded-full object-cover" />}
              <div>
                <p className="text-white font-semibold leading-tight">{cast.host_name}</p>
                {cast.host_title && <p className="text-xs text-[#94A3B8]">{cast.host_title}</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Register form */}
      <div className="lg:col-span-5 space-y-5">
        <CustomEmbedBlock cast={cast} placement="above_form" />
        <RegistrationForm cast={cast} onRegistered={onRegistered} onTrack={onTrack} />
        <CustomEmbedBlock cast={cast} placement="below_form" />
      </div>
    </div>
  );
}

/** Renders the customer's 3rd-party widget for a given placement slot. */
function CustomEmbedBlock({ cast, placement }) {
  const code = cast?.custom_embed_code?.trim();
  const slot = cast?.custom_embed_placement || "below_form";
  if (!code || slot !== placement) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5" data-testid={`livecast-custom-embed-${placement}`}>
      {cast.custom_embed_label && (
        <p className="text-xs uppercase tracking-[0.18em] text-[#F4B41A] font-bold mb-3">
          {cast.custom_embed_label}
        </p>
      )}
      <EmbedRenderer html={code} />
    </div>
  );
}

function RegistrationForm({ cast, onRegistered, onTrack }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city: "", state: "", business_category: "", question: "", consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => { onTrack("livecast_form_start", cast); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.phone || !form.city || !form.state) {
      toast.error("Please fill in your name, email, phone, city, and state.");
      return;
    }
    setSubmitting(true);
    onTrack("livecast_form_submit", cast);
    try {
      const res = await axios.post(`${API}/v1/livecasts/register`, {
        livecast_id: cast.id,
        ...form,
        watch_mode: "watch_now",
        utm: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
      });
      onRegistered(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#F4B41A]/30 bg-gradient-to-br from-[#F4B41A]/[0.06] to-transparent p-5 sm:p-6 space-y-3" data-testid="livecast-register-form">
      <div className="text-center mb-2">
        <Sparkles className="w-6 h-6 text-[#F4B41A] mx-auto mb-2" />
        <h3 className="text-xl font-bold text-white">Reserve Your Spot</h3>
        <p className="text-xs text-[#94A3B8] mt-1">Enter your details to watch now.</p>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} placeholder="First name *" className="bg-[#0F172A] border-white/10 text-white" required data-testid="reg-first-name" />
        <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} placeholder="Last name" className="bg-[#0F172A] border-white/10 text-white" />
      </div>
      <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email *" className="bg-[#0F172A] border-white/10 text-white" required data-testid="reg-email" />
      <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone *" type="tel" className="bg-[#0F172A] border-white/10 text-white" required data-testid="reg-phone" />
      <div className="grid grid-cols-2 gap-2.5">
        <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="City *" className="bg-[#0F172A] border-white/10 text-white" required data-testid="reg-city" />
        <Input value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="State *" className="bg-[#0F172A] border-white/10 text-white" required data-testid="reg-state" />
      </div>
      {cast.lead_form?.show_business_category && (
        <Input value={form.business_category} onChange={(e) => update("business_category", e.target.value)} placeholder="Business category" className="bg-[#0F172A] border-white/10 text-white" />
      )}
      {cast.lead_form?.show_question && (
        <Textarea value={form.question} onChange={(e) => update("question", e.target.value)} rows={2} placeholder="Question for the host (optional)" className="bg-[#0F172A] border-white/10 text-white" />
      )}
      <label className="flex items-start gap-2 text-xs text-[#94A3B8] mt-1">
        <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="mt-0.5 accent-[#F4B41A]" />
        <span>I agree to receive event reminders & follow-ups via SMS/email. Reply STOP to unsubscribe.</span>
      </label>
      <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-bold py-6 text-base shadow-[0_0_30px_rgba(244,180,26,0.35)]" data-testid="reg-submit">
        {submitting ? "Reserving…" : <>Watch Now <ArrowRight className="w-4 h-4 ml-1.5" /></>}
      </Button>
      <p className="text-[10px] text-[#64748B] text-center">Powered by LocalBest.ai · Your data is secure.</p>
    </form>
  );
}

/* ============================================================== */
/* Watch room (after registration)                                  */
/* ============================================================== */
function WatchRoom({ cast, registration, onTrack }) {
  const businessId = cast.business?.id;
  const offer = cast.offer || {};
  const hasOffer = offer.headline || offer.button_url;

  useEffect(() => {
    onTrack("livecast_video_start", cast);
    // Watch milestones (simulated, since iframe doesn't expose progress)
    const t25 = setTimeout(() => onTrack("livecast_watch_25", cast), 25000);
    const t50 = setTimeout(() => onTrack("livecast_watch_50", cast), 50000);
    return () => { clearTimeout(t25); clearTimeout(t50); };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="grid lg:grid-cols-12 gap-5" data-testid="livecast-watch-room">
      {/* Main video */}
      <div className="lg:col-span-8 space-y-4">
        <div className="rounded-2xl overflow-hidden border border-[#F4B41A]/30 bg-black shadow-[0_30px_80px_-30px_rgba(244,180,26,0.35)]">
          <div className="aspect-video bg-black">
            {cast.video?.embed_url ? (
              <iframe
                src={`${cast.video.embed_url}${cast.video.embed_url.includes("?") ? "&" : "?"}autoplay=1`}
                title={cast.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                style={{ border: 0 }}
              />
            ) : cast.video?.url && /\.(mp4|webm|mov|m3u8)(\?|$)/i.test(cast.video.url) ? (
              <video src={cast.video.url} autoPlay controls className="w-full h-full" playsInline />
            ) : cast.video?.url ? (
              // Fallback for non-embeddable providers (Twitch/Kick/LinkedIn/Rumble/etc.) — branded "Watch Replay" button
              <a
                href={cast.video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#0F172A] via-[#0B1226] to-[#1F0F2E] hover:from-[#0F172A] hover:to-[#2563EB]/30 transition"
                data-testid="livecast-external-watch"
                onClick={() => onTrack("livecast_external_watch_click", cast)}
              >
                <span className="inline-flex w-16 h-16 rounded-full bg-gradient-to-br from-[#F4B41A] to-[#E0A015] items-center justify-center shadow-[0_0_30px_rgba(244,180,26,0.35)] mb-4">
                  <ArrowRight className="w-7 h-7 text-[#0A0F1C]" />
                </span>
                <p className="text-xs uppercase tracking-[0.3em] text-[#F4B41A] font-bold mb-2">
                  Watch Replay
                </p>
                <p className="text-white text-lg font-semibold leading-snug max-w-md">
                  Tap to open {cast.title}
                </p>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Opens on the hosting platform in a new tab
                </p>
              </a>
            ) : null}
          </div>
        </div>

        {/* Title + share */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#F4B41A] font-bold mb-1">Best of Best LiveCast™ · Watching Now</p>
            <h1 className="text-2xl font-bold text-white">{cast.title}</h1>
          </div>
          <ShareButton cast={cast} onShare={() => onTrack("livecast_share_click", cast)} />
        </div>

        {/* Offer card */}
        {hasOffer && (
          <a
            href={offer.button_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onTrack("livecast_cta_click", cast)}
            className="block rounded-2xl border border-[#F4B41A]/40 bg-gradient-to-r from-[#F4B41A]/[0.12] via-[#0F172A] to-[#1E40AF]/[0.10] p-5 hover:border-[#F4B41A]/70 transition-all group"
            data-testid="livecast-offer-cta"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#F4B41A] to-[#E0A015] flex items-center justify-center shadow-lg">
                <Award className="w-6 h-6 text-[#0A0F1C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#F4B41A] font-bold mb-0.5">Exclusive Live Offer</p>
                <p className="text-white font-bold text-lg leading-tight">{offer.headline || "Special offer"}</p>
                {offer.description && <p className="text-[#94A3B8] text-sm mt-0.5">{offer.description}</p>}
              </div>
              <span className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full bg-[#F4B41A] text-[#0A0F1C] font-bold text-sm group-hover:scale-105 transition-transform flex-shrink-0">
                {offer.button_text || "Claim Offer"} <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          </a>
        )}

        {/* Host card */}
        {(cast.host_name || cast.host_title) && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10">
            {cast.host_avatar_url && <img src={cast.host_avatar_url} alt={cast.host_name} className="w-11 h-11 rounded-full object-cover" />}
            <div>
              <p className="text-white font-semibold">{cast.host_name}</p>
              <p className="text-xs text-[#94A3B8]">{cast.host_title} · {cast.business?.name}</p>
            </div>
          </div>
        )}

        {/* Custom 3rd-party embed (after-watch placement) */}
        <CustomEmbedBlock cast={cast} placement="after_watch" />
      </div>

      {/* Side panel: AI chat */}
      <div className="lg:col-span-4">
        {cast.enable_ai_assistant && businessId && (
          <AIChatPanel
            businessId={businessId}
            businessName={cast.business?.name}
            registrantName={registration?.first_name || ""}
            onAsk={() => onTrack("livecast_ai_question", cast)}
          />
        )}
      </div>
    </div>
  );
}

function ShareButton({ cast, onShare }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
    onShare?.();
  };
  return (
    <Button onClick={copy} variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full" data-testid="livecast-share-btn">
      <Share2 className="w-4 h-4 mr-1.5" /> Share
    </Button>
  );
}

/* ============================================================== */
/* AI Chat side panel (reuses /api/chat which powers AI Hero)       */
/* ============================================================== */
function AIChatPanel({ businessId, businessName, registrantName, onAsk }) {
  const sessionId = useRef(`lc-chat-${Date.now()}`).current;
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hi${registrantName ? `, ${registrantName}` : ""}! I'm the AI Event Assistant for ${businessName || "this business"}. Ask me anything about today's session, the offer, or how to take the next step.` },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setLoading(true);
    onAsk?.();
    try {
      const r = await axios.post(`${API}/chat`, { message: msg, business_id: businessId, session_id: sessionId });
      setMessages((p) => [...p, { role: "assistant", content: r.data.response }]);
    } catch {
      setMessages((p) => [...p, { role: "assistant", content: "I'm having trouble connecting. The host's team will follow up with you shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = ["How does this work?", "What's the offer?", "Can I get a demo?"];

  return (
    <div className="rounded-2xl border border-[#1E40AF]/40 bg-gradient-to-b from-[#1E40AF]/[0.10] to-transparent flex flex-col h-[600px]" data-testid="livecast-ai-panel">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E40AF] to-[#2563EB] flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm">AI Event Assistant</p>
          <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full" /> Online · Powered by LocalBest.ai
          </p>
        </div>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <div className="w-7 h-7 rounded-full bg-[#1E40AF]/25 border border-[#1E40AF]/40 flex items-center justify-center flex-shrink-0"><Bot className="w-3.5 h-3.5 text-[#7C9CFF]" /></div>}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#F4B41A] text-[#0A0F1C] font-medium" : "bg-white/[0.06] text-[#E0E7F0] border border-white/10"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div className="flex gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#7C9CFF]" /><span className="text-xs text-[#94A3B8]">Thinking…</span></div>}
      </div>
      {messages.length <= 2 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button key={s} onClick={() => setInput(s)} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/10 text-[#C7D2E0] border border-white/10">
              {s}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the AI assistant…" className="bg-[#020817] border-white/10 text-white text-sm" disabled={loading} data-testid="ai-chat-input" />
        <Button type="submit" disabled={loading || !input.trim()} className="bg-[#1E40AF] hover:bg-[#2563EB] text-white" data-testid="ai-chat-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
